/**
 * system-config.ts — Configuração centralizada do sistema STRADA.
 *
 * Hooks e helpers para consumir dados de referência da BD:
 * - Enum labels (cores, labels, ícones para todos os status/tipos)
 * - Lookup values (dados configuráveis por escola)
 * - Categorias de licença (referência global)
 * - Países (referência global)
 *
 * Todos os dados são cacheados por 30 minutos via React Query.
 */

import { useQuery } from '@tanstack/react-query'
import { api } from './api'

// ── Types ───────────────────────────────────────────────────────────────────

export interface EnumLabel {
  id: string
  grupo: string
  valor: string
  label: string
  cor: string | null
  bgCor: string | null
  icone: string | null
  ordem: number
}

export interface LookupValue {
  id: string
  schoolId: string
  grupo: string
  valor: string
  label: string
  meta: Record<string, any> | null
  ativo: boolean
  ordem: number
}

export interface CategoriaLicenca {
  id: string
  codigo: string
  nome: string
  descricao: string | null
  idadeMinima: number | null
  ativo: boolean
  ordem: number
}

export interface Pais {
  id: string
  codigo: string
  nome: string
  bandeira: string | null
  codigoTelefone: string | null
  ativo: boolean
  ordem: number
}

// ── Query config ────────────────────────────────────────────────────────────

const STALE_30M = 30 * 60 * 1000
const GC_1H = 60 * 60 * 1000

// ── Hooks ───────────────────────────────────────────────────────────────────

/** Todos os labels de enums do sistema, agrupados por grupo */
export function useEnumLabels() {
  return useQuery<Record<string, EnumLabel[]>>({
    queryKey: ['system-enum-labels'],
    queryFn: () => api.get('/configuracoes/enum-labels').then(r => r.data),
    staleTime: STALE_30M,
    gcTime: GC_1H,
  })
}

/** Lista de países (referência global) */
export function usePaises() {
  return useQuery<Pais[]>({
    queryKey: ['system-paises'],
    queryFn: () => api.get('/configuracoes/paises').then(r => r.data),
    staleTime: STALE_30M,
    gcTime: GC_1H,
  })
}

/** Categorias de carta de condução (referência global) */
export function useCategoriasLicenca() {
  return useQuery<CategoriaLicenca[]>({
    queryKey: ['system-categorias-licenca'],
    queryFn: () => api.get('/configuracoes/categorias-licenca').then(r => r.data),
    staleTime: STALE_30M,
    gcTime: GC_1H,
  })
}

/** Valores de lookup por escola (tipo_documento, tipo_veiculo, tipo_sala, etc.) */
export function useLookupValues(schoolId: string | undefined, grupo: string) {
  return useQuery<LookupValue[]>({
    queryKey: ['lookup-values', schoolId, grupo],
    queryFn: () => api.get(`/schools/${schoolId}/configuracoes/lookups/${grupo}`).then(r => r.data),
    enabled: !!schoolId,
    staleTime: STALE_30M,
    gcTime: GC_1H,
  })
}

// ── Helpers para construir mapas de dados ────────────────────────────────────

type EnumInfo = { label: string; cor: string; bgCor: string; icone: string | null }

/** Constrói Record<valor, { label, cor, bgCor, icone }> para um grupo de enum */
export function buildEnumMap(
  labels: Record<string, EnumLabel[]> | undefined,
  grupo: string,
): Record<string, EnumInfo> {
  const map: Record<string, EnumInfo> = {}
  labels?.[grupo]?.forEach(l => {
    map[l.valor] = {
      label: l.label,
      cor: l.cor || '#6b7280',
      bgCor: l.bgCor || 'rgba(107,114,128,0.1)',
      icone: l.icone,
    }
  })
  return map
}

/** Constrói Record<valor, label> simples para um grupo de enum */
export function buildLabelMap(
  labels: Record<string, EnumLabel[]> | undefined,
  grupo: string,
): Record<string, string> {
  const map: Record<string, string> = {}
  labels?.[grupo]?.forEach(l => { map[l.valor] = l.label })
  return map
}

/** Obtém um único enum label */
export function getEnumLabel(
  labels: Record<string, EnumLabel[]> | undefined,
  grupo: string,
  valor: string,
): EnumLabel | undefined {
  return labels?.[grupo]?.find(l => l.valor === valor)
}

/** Record<valor, label> a partir de lookup values */
export function buildLookupLabelMap(values: LookupValue[] | undefined): Record<string, string> {
  const map: Record<string, string> = {}
  values?.forEach(v => { map[v.valor] = v.label })
  return map
}

/** Record<codigo, { nome, bandeira }> a partir de países */
export function buildPaisMap(paises: Pais[] | undefined): Record<string, { nome: string; bandeira: string }> {
  const map: Record<string, { nome: string; bandeira: string }> = {}
  paises?.forEach(p => { map[p.codigo] = { nome: p.nome, bandeira: p.bandeira || '' } })
  return map
}

/** Record<codigo, nome> a partir de categorias de licença */
export function buildCategoriaLicencaMap(categorias: CategoriaLicenca[] | undefined): Record<string, string> {
  const map: Record<string, string> = {}
  categorias?.forEach(c => { map[c.codigo] = c.nome })
  return map
}

/** Mapeia cor hex da BD para badge CSS class existente (bridge) */
export function enumBadgeClass(cor: string | null): string {
  if (!cor) return 'badge-gray'
  if (cor.includes('22c55e') || cor.includes('16a34a')) return 'badge-green'
  if (cor.includes('3b82f6') || cor.includes('2563eb')) return 'badge-blue'
  if (cor.includes('f59e0b') || cor.includes('d97706')) return 'badge-yellow'
  if (cor.includes('ef4444') || cor.includes('dc2626')) return 'badge-red'
  if (cor.includes('f97316') || cor.includes('ea580c')) return 'badge-orange'
  if (cor.includes('8b5cf6')) return 'badge-purple'
  return 'badge-gray'
}
